import { AxiosPromise } from 'axios';
import cn from 'classnames';
import { omit } from 'lodash';
import { DateTime } from 'luxon';
import React, {
  ChangeEvent,
  FC,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withTranslation } from 'react-i18next';

import {
  Button,
  Checkbox,
  Table,
  getCheckboxCheckedValue,
  useStateWithCallback,
  LibFixedType,
  IconFile,
  ILibModalSizes,
} from '@infologistics/frontend-libraries';

import CertificatePower from '../CertificatePower';

import { updateUserSettings } from '@app/store/modules/user';
import CryptoProProvider from '../CryptoPro/CryptoProProvider';
import ModalWrapper from '@app/common/ModalWrapper';

import { useLoading } from '@app/hooks/useLoading';

import { displayErrorNotification } from '@utils/utils';
import {
  getCertificateData,
  getCertificateKey,
  getCertificateValidity,
} from '../utils';
import { BrowserStorage, Modal, UserSettingsKey } from '@const/consts';
import { AppState } from '@app/store/types';
import {
  ICertificates,
  ICloudCertificate,
  ILocalCertificate,
  ISuccessfulRequest,
  IUserCertificate,
} from '../types';
import { Nullable } from '@app/types';

import {
  Certificate,
  CertificateKeys,
  ICertificateCheckboxKeyValue,
  ICertificateSettingsProps as IProps,
  ISelectedCertificate,
} from './types';
import { IUserSettings } from '@store/modules/user/types';

const initialCertificates = {
  cloud: {},
  local: [],
  user: {},
};

import styles from './CertificateSettings.module.css';
import { useAppSelector } from '@app/hooks/redux';
import { useParams } from 'react-router';

const CertificateSettings: FC<IProps> = (props) => {
  const {
    t,
    isReceipts,
    onCloseModal,
    onAccessPluginStatusChange,
    onSaveSelectedCertificates,
    withWrapper,
    isAccessPlugin,
  } = props;

  const {
    mandates,
    profile: userProfile,
    settings: userSettings,
  } = useSelector((state: AppState) => state.user);

  const { boxOguid } = useParams();
  const personalBoxOguid = useAppSelector((state) => state.user.profile.boxOguid);
  const currentBoxOguid = boxOguid ?? personalBoxOguid;

  const mandatesCount = mandates.filter(
    (val) => val.boxOguid === currentBoxOguid
  ).length;
  const orgOguid = 'orgOguid';

  const dispatch = useDispatch<any>();

  const [isLoadingLocalCertificates, setIsLoadingLocalCertificates] =
    useState<boolean>(false);
  const [isLoadingUserCertificates] = useState<boolean>(false);
  const [isShowCertificatePowersSettings, setIsShowCertificatePowersSettings] =
    useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [activeSelectedCertificate, setActiveSelectedCertificate] =
    useStateWithCallback<Nullable<ISelectedCertificate>>(null);
  const [selectedCertificate, setSelectedCertificate] = useState<
    ISelectedCertificate[]
  >([]);
  const [certificates, setCertificates] =
    useStateWithCallback<ICertificates>(initialCertificates);

  // const isEnabledCloudCertificates = false /** DSS certificates are temporarily disabled */
  const hasNotError = errorMessage.length === 0;

  const showLocalCertificates =
    !isLoadingLocalCertificates && hasNotError && certificates.local.length > 0;
  // const showCloudCertificates = isEnabledCloudCertificates && !isLoadingCloudCertificates && hasNotError && certificates.cloud[userProfile.oguid]?.length > 0
  const showUserCertificates =
    !isLoadingUserCertificates &&
    hasNotError &&
    orgOguid in certificates.user &&
    userProfile.oguid in certificates.user[orgOguid] &&
    !isReceipts;
  const certificatesFromStore = userSettings?.selectedCertificates;

  useEffect(() => {
    getCertificates();
    const selected: ISelectedCertificate[] | undefined = certificatesFromStore;

    if (selected) {
      setSelectedCertificate(
        selected.map((certificate) =>
          certificate.orgsStatus
            ? certificate
            : {
                ...certificate,
                orgsStatus: {
                  [orgOguid]: false,
                },
              }
        )
      );
    }
  }, []);

  const setError = (errorMessage: string): void => {
    setErrorMessage(errorMessage);
  };

  const Crypto: CryptoProProvider = new CryptoProProvider(setError);

  const checkedPlugin = (): void => {
    setIsLoadingLocalCertificates(false);
    onAccessPluginStatusChange?.(false);
  };

  const setLocalCertificates = (response: ILocalCertificate[]): void => {
    setIsLoadingLocalCertificates(false);

    setCertificates((prevState: ICertificates) => {
      return {
        ...prevState,
        local: [...response],
      };
    }, saveCertificatesToLocalStorage);
  };

  // const setCloudCertificates = (response: ICloudCertificate[]): void => {
  //   setIsLoadingCloudCertificates(false)

  //   setCertificates({
  //     ...certificates,
  //     cloud: {
  //       ...certificates.cloud,
  //       [userProfile.oguid]: response.map((certificate) =>
  //         pick(certificate, ['CN', 'certificateBase64', 'dateFrom', 'dateTo', 'id'])
  //       )
  //     }
  //   }, saveCertificatesToLocalStorage)
  // }

  // const setUserCertificates = (response: Nullable<IUserCertificate>): void => {
  //   setIsLoadingUserCertificates(false)

  //   if (!response) return

  //   setCertificates((prevState: ICertificates) => {
  //     return {
  //       ...prevState,
  //       user: {
  //         ...prevState.user,
  //         [orgOguid]: {
  //           ...prevState.user[orgOguid],
  //           [userProfile.oguid]: response
  //         }
  //       }
  //     }
  //   })
  // }

  const saveCertificatesToLocalStorage = (
    certificates: ICertificates
  ): void => {
    localStorage.setItem(
      BrowserStorage.CERTIFICATES,
      JSON.stringify(omit(certificates, 'user'))
    );
  };

  const getLocalCertificates = async (): Promise<void> => {
    const { cadesplugin } = window as any;
    try {
      await cadesplugin;

      await Crypto.getCertificates(setLocalCertificates).catch(checkedPlugin);
    } catch (_err) {
      checkedPlugin();
    }
  };

  // const getCloudCertificates = async (): Promise<void> => {
  //   const requestId = uuidv4()

  //   setRequestId?.(requestId)

  //   try {
  //     const {
  //       data: { certificates }
  //     } = await dssService.getDssCertificates(requestId)

  //     setCloudCertificates(certificates)
  //   } catch (err: any) {
  //     displayErrorNotification(err)

  //     setCertificates({
  //       ...certificates,
  //       cloud: {}
  //     })
  //     setIsLoadingCloudCertificates(false)
  //   }
  // }

  // const getUserCertificates = async (): Promise<IUserCertificates> => {
  //   const requestId = uuidv4()

  //   setRequestId?.(requestId)

  //   const userCertificates: IUserCertificates = {}

  //   try {
  //     const { data } = await dcsService.getUserCertificate(userProfile.oguid, requestId)

  //     if (data) {
  //       userCertificates[orgOguid] = {
  //         ...userCertificates[orgOguid],
  //         [userProfile.oguid]: data
  //       }
  //     }

  //     setUserCertificates(data)
  //   } catch (err: any) {
  //     displayErrorNotification(err)
  //   }

  //   return userCertificates
  // }

  const getCertificates = async (): Promise<void> => {
    // setIsLoadingCloudCertificates(isEnabledCloudCertificates && true)
    setIsLoadingLocalCertificates(true);

    Promise.all([
      // isEnabledCloudCertificates && getCloudCertificates(),
      getLocalCertificates(),
    ]).catch(displayErrorNotification);
  };

  const getAllCertificates = (allCertificates: ICertificates): Certificate[] =>
    Object.values(allCertificates).reduce(
      (
        acc: Certificate[],
        certificates: ICloudCertificate | ILocalCertificate[] | IUserCertificate
      ) => {
        if (Array.isArray(certificates))
          certificates.forEach((element: ILocalCertificate) =>
            acc.push(element)
          );

        if ('id' in certificates) acc.push(certificates);

        if (
          orgOguid in certificates &&
          certificates[orgOguid]?.[userProfile.oguid]
        )
          acc.push(certificates[orgOguid][userProfile.oguid]);

        return acc;
      },
      []
    );

  const checkIsAllCertificatesSelected = (): boolean => {
    const allCertificates = getAllCertificates(certificates);

    if (!allCertificates.length) return false;

    return allCertificates.every((certificate) => {
      const key = getCertificateKey(certificate);

      const existingCertificate = selectedCertificate.find(
        ({ certificate: selectedCertificate }) =>
          selectedCertificate[key] === certificate[key]
      );

      if (!existingCertificate) return false;

      return existingCertificate.orgsStatus[orgOguid];
    });
  };

  const renderSelectAllCheckbox = (): ReactNode => (
    <Checkbox
      checked={getCheckboxCheckedValue(checkIsAllCertificatesSelected())}
      name='selectAllCertificates'
      onChange={handleSelectAllCertificates}
      value='selectAllCertificates'
    />
  );

  const getCheckedCertificate = (
    currentCertificate: Certificate,
    key: CertificateKeys
  ): ISelectedCertificate | undefined =>
    selectedCertificate.find(({ certificate, orgsStatus }) =>
      Object.keys(certificate).some(
        () =>
          certificate[key] === currentCertificate[key] && orgsStatus[orgOguid]
      )
    );

  const getCertificateKeyValue = (
    certificate: Certificate
  ): ICertificateCheckboxKeyValue => {
    if ('id' in certificate) {
      const { id: value } = certificate;

      return {
        key: 'id',
        value,
      };
    }

    if ('validToDate' in certificate) {
      const { serialNumber: value } = certificate;

      return {
        key: 'serialNumber',
        value,
      };
    }

    const { oguid: value } = certificate;

    return {
      key: 'oguid',
      value,
    };
  };

  const handleCertificatePowersSettingsClose = (): void => {
    setIsShowCertificatePowersSettings(false);
    setActiveSelectedCertificate(null);
  };

  const handleSelectCertificate =
    (currentCertificate: Certificate, key: CertificateKeys) =>
    (evt: ChangeEvent<HTMLInputElement>): void => {
      const { checked } = evt.target;

      /**
       * If the current certificate is already in the selected ones, change the activity status
       */
      const updatedCertificate = selectedCertificate.find(
        ({ certificate }) => certificate[key] === currentCertificate[key]
      );
      if (updatedCertificate) {
        const updatedCertificates = selectedCertificate.map((item) => {
          const { certificate, orgsStatus } = item;

          if (certificate[key] === currentCertificate[key]) {
            return {
              ...item,
              orgsStatus: {
                ...orgsStatus,
                [orgOguid]: checked,
              },
            };
          }

          return item;
        });

        setSelectedCertificate(updatedCertificates);
        return;
      }

      const allCertificates = getAllCertificates(certificates);

      const checkedCertificate = allCertificates.reduce(
        (acc: ISelectedCertificate[], certificate: Certificate) => {
          const selected = certificate[key] === currentCertificate[key];

          return selected
            ? [
                ...acc,
                {
                  certificate,
                  orgsStatus: {
                    [orgOguid]: checked,
                  },
                  powers: {},
                },
              ]
            : acc;
        },
        []
      );

      setSelectedCertificate([...selectedCertificate, ...checkedCertificate]);
    };

  const handleSelectAllCertificates = (
    evt: ChangeEvent<HTMLInputElement>
  ): void => {
    const { checked } = evt.target;

    const allCertificates = getAllCertificates(certificates);

    const newSelectedCertificates = [...selectedCertificate];

    allCertificates.forEach((certificate) => {
      const key = getCertificateKey(certificate);

      const existingCertificateIndex = newSelectedCertificates.findIndex(
        ({ certificate: selectedCertificate }) =>
          selectedCertificate[key] === certificate[key]
      );

      if (existingCertificateIndex !== -1) {
        const { orgsStatus } =
          newSelectedCertificates[existingCertificateIndex];

        newSelectedCertificates[existingCertificateIndex].orgsStatus = {
          ...orgsStatus,
          [orgOguid]: checked,
        };

        return;
      }

      newSelectedCertificates.push({
        certificate,
        orgsStatus: {
          [orgOguid]: checked,
        },
        powers: {},
      });
    });

    setSelectedCertificate(newSelectedCertificates);
  };

  const handlePoAButtonClick = (certificate: ISelectedCertificate) => () => {
    setActiveSelectedCertificate(certificate, () => {
      setIsShowCertificatePowersSettings(true);
    });
  };

  const renderPoA = (currentCertificate: Certificate): ReactElement => {
    // const renderPoA = (): ReactElement => {
    const key = getCertificateKey(currentCertificate);

    const existingCertificate = selectedCertificate.find(
      ({ certificate }) => certificate[key] === currentCertificate[key]
    );

    const selectedCertificateTemplate = {
      certificate: currentCertificate,
      orgsStatus: {
        [orgOguid]: false,
      },
      powers: {},
    };

    const certificate = existingCertificate ?? selectedCertificateTemplate;

    return (
      <div className='d-flex align-items-center justify-content-start'>
        {!!mandatesCount && <IconFile classes='mr-1 flex-shrink-0' />}
        <Button
          classes={styles.powerOfAttorney_button}
          theme='link'
          linkStyle='none'
          onClick={handlePoAButtonClick(certificate)}
        >
          {mandatesCount
            ? mandatesCount + ' ' + t('profile:PoA.units')
            : t('profile:PoA.add')}
        </Button>
      </div>
    );
  };

  const renderCheckbox = (certificate: Certificate): ReactElement => {
    const { key, value } = getCertificateKeyValue(certificate);

    return (
      <Checkbox
        checked={getCheckboxCheckedValue(
          !!getCheckedCertificate(certificate, key)
        )}
        className='mr-1'
        name='selectCertificate'
        onChange={handleSelectCertificate(certificate, key)}
        value={`${value}`}
      />
    );
  };

  const renderTableRow = (certificate: Certificate): ReactElement => {
    const { dateFrom, dateTo, info, key } = getCertificateData(
      certificate,
      Crypto,
      t
    );

    const classes =
      dateTo && +DateTime.fromISO(dateTo).toMillis() < +Date.now()
        ? 'text-danger'
        : '';

    return (
      <tr key={key}>
        <td>{renderCheckbox(certificate)}</td>
        <td>
          <p className={classes}>{info ?? t('common:notSpecifiedAlt')}</p>
        </td>
        <td>
          <p className={classes}>
            {getCertificateValidity(dateFrom, dateTo, Crypto, t)}
          </p>
        </td>
        <td className={styles.powerOfAttorney}>
          {renderPoA(certificate)}
          {/* {renderPoA()} */}
        </td>
      </tr>
    );
  };

  // const renderCloudCertificates = (): ReactNode => {
  //   return certificates.cloud[userProfile.oguid].map((certificate: ICloudCertificate) => renderTableRow(certificate))
  // }

  const renderLocalCertificates = (): ReactNode => {
    return certificates.local.map((certificate: ILocalCertificate) =>
      renderTableRow(certificate)
    );
  };

  const renderUserCertificates = (): ReactNode => {
    const certificate = certificates.user[orgOguid][userProfile.oguid];

    if (!certificate) return;

    return renderTableRow(certificate);
  };

  const tableHeadings = [
    { content: renderSelectAllCheckbox() },
    { content: t('envelopes:signAction.certificate.table.owner') },
    { content: t('envelopes:signAction.certificate.table.validity') },
    { content: t('envelopes:signAction.certificate.PoA.title') },
  ];

  const handleSuccess = () => {
    onSaveSelectedCertificates?.(selectedCertificate);
    onCloseModal();
  };

  const handleCertificateSave = (data?: IUserSettings): AxiosPromise => {
    if (!data) return Promise.reject();

    return dispatch(updateUserSettings(data)).unwrap();
  };

  const [isSaving, saveCertificate] = useLoading<
    IUserSettings,
    ISuccessfulRequest
  >({
    fetchRequest: handleCertificateSave,
    notification: {
      title: t('notification:successTitle'),
      content: t('notification:successContent'),
    },
  });

  const handleSaveCertificatesClick = (): void => {
    saveCertificate({
      data: {
        ...userSettings,
        [UserSettingsKey.SELECTED_CERTIFICATES]: selectedCertificate,
      },
      callback: handleSuccess,
      errorCallback: displayErrorNotification,
    });
  };

  const renderUnavailableMessage = (): ReactNode => (
    <h4 className='text-danger mt-3'>
      {t('envelopes:signAction.unavailable')}
    </h4>
  );

  const renderContent = (): ReactElement =>
    isShowCertificatePowersSettings && activeSelectedCertificate ? (
      <CertificatePower
        isEdit
        onClose={handleCertificatePowersSettingsClose}
        boxOguid={currentBoxOguid}
        // onSave={handleCertificatePowerSave}
      />
    ) : (
      <>
        <h4 className='modal-header'>
          {t('envelopes:signAction.certificate.selectCertificate')}
        </h4>

        <div className={cn(styles.body, 'p-3 scrollbar-content relative')}>
          {!isAccessPlugin && renderUnavailableMessage()}

          <Button
            onClick={getCertificates}
            isDisabled={
              isLoadingLocalCertificates ||
              // || isLoadingCloudCertificates
              isLoadingUserCertificates
            }
          >
            {t('envelopes:signAction:certificate.refresh')}
          </Button>
          <Table
            fixedType={LibFixedType.MODAL}
            fixedModalHeight='168px'
            headings={tableHeadings}
            isUpdateTableY
          >
            <>
              {showLocalCertificates && renderLocalCertificates()}
              {/* {showCloudCertificates && renderCloudCertificates()} */}
              {showUserCertificates && renderUserCertificates()}
            </>
          </Table>
        </div>

        <div className='d-flex modal-footer'>
          <Button
            classes='ml-auto'
            onClick={handleSaveCertificatesClick}
            theme='primary'
            loading={isSaving}
          >
            {t('common:save')}
          </Button>
          <Button classes='ml-4' onClick={onCloseModal}>
            {t('common:cancel')}
          </Button>
        </div>
      </>
    );

  return withWrapper ? (
    <ModalWrapper
      backdrop={false}
      keyboard={false}
      id={Modal.CERTIFICATE_SETTINGS}
      closeText={t('common:close')}
      size={ILibModalSizes.MEDIUM}
    >
      {renderContent()}
    </ModalWrapper>
  ) : (
    renderContent()
  );
};

export default withTranslation()(CertificateSettings);
